import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setDialogOpen, setSelectedEvent } from '../../store/Actions';
import { buildDetailPath, buildSectionPath, useAppNavigate } from '../../utils/Routing';
import { MyAvatar } from '../common/MyAvatar';
import { buildTripSummaries, formatDateRange } from '../../utils/EntitySummaries';
import {
  HeroBar, StatTile, YearChart,
  CategoryRankRow, EventRow,
} from './DetailViewParts';
import { EntityEvents } from './EntityEvents';
import {
  buildYearBreakdown, rankByEvents, findTopPlace,
  buildRecentEvents, getEventStartAt, computeDaySpan,
  countDistinctPlaces, formatLongDate,
} from './insightHelpers';
import './DetailView.scss';

export function TripDetailView({entityId, tab = 'summary'}) {
  const trips = useSelector(state => state.viewingUserTrips) || {};
  const events = useSelector(state => state.viewingUserEvents) || {};
  const friends = useSelector(state => state.viewingUserFriends) || {};
  const categories = useSelector(state => state.viewingUserCategories) || {};
  const viewingUserAccount = useSelector(state => state.viewingUserAccount);
  const loggedInUserAccount = useSelector(state => state.loggedInUserAccount);
  const isOwnMap = viewingUserAccount && loggedInUserAccount
    && viewingUserAccount.id === loggedInUserAccount.id;
  const dispatch = useDispatch();
  const navigateTo = useAppNavigate();

  const trip = trips[entityId];
  const username = viewingUserAccount?.username;

  useEffect(() => {
    if(!trip && username){
      navigateTo(buildSectionPath(username, 'Trips'));
    }
  }, [trip, username, navigateTo]);

  // Events that belong to this trip — sorted chronologically for the
  // recent-events list and the first/last/day-span stats.
  const tripEvents = useMemo(() => {
    return Object.values(events)
      .filter(e => (e.tripIds || []).includes(entityId))
      .sort((a, b) => (getEventStartAt(a) || 0) - (getEventStartAt(b) || 0));
  }, [events, entityId]);

  const tripSummary = useMemo(() => buildTripSummaries(events)[entityId], [events, entityId]);
  const yearEntries = useMemo(() => buildYearBreakdown(tripEvents), [tripEvents]);
  const topCategories = useMemo(
    () => rankByEvents(tripEvents, e => e.categoryId, categories),
    [tripEvents, categories]
  );
  const tripFriendIds = (tripSummary?.friendIds || []).filter(id => friends[id]);
  const topPlace = useMemo(() => findTopPlace(tripEvents), [tripEvents]);
  const recentEvents = useMemo(() => buildRecentEvents(tripEvents, 8), [tripEvents]);
  const placeCount = useMemo(() => countDistinctPlaces(tripEvents), [tripEvents]);
  const daySpan = useMemo(() => computeDaySpan(tripEvents), [tripEvents]);

  const sortedDates = useMemo(() => {
    return tripEvents.map(getEventStartAt).filter(Boolean).sort((a, b) => a - b);
  }, [tripEvents]);
  const firstDate = sortedDates[0] || null;
  const lastDate = sortedDates[sortedDates.length - 1] || null;

  if(!trip){
    return (
      <main className="detail-view">
        <div className="detail-view__not-found">
          <div>This trip couldn't be found.</div>
        </div>
      </main>
    );
  }

  const goBack = () => {
    if(!username) return;
    navigateTo(buildSectionPath(username, 'Trips'));
  };

  const selectTab = (nextTab) => {
    if(!username || nextTab === tab) return;
    navigateTo(buildDetailPath(username, 'Trips', trip.id, nextTab));
  };

  const openEdit = () => {
    dispatch(setDialogOpen('Edit Trip', {
      parentDialog: '',
      listDialog: null,
      dialogTitle: `Edit Trip: ${trip.name}`,
      trip,
    }));
  };

  const openNewEvent = () => {
    if(!loggedInUserAccount) return;
    const categoryList = Object.values(categories);
    if(categoryList.length === 0){
      alert('Create a category first.');
      return;
    }
    // Prefill the new event with the start date, friends, and tags from the
    // most recent event on this trip so the user doesn't have to re-enter
    // them when adding successive events to the same trip.
    const mostRecentEvent = tripEvents.length > 0
      ? tripEvents[tripEvents.length - 1]
      : null;
    const newEvent = {
      userId: loggedInUserAccount.id,
      title: '',
      categoryId: categoryList[0].id,
      tripIds: [trip.id],
      ...(mostRecentEvent?.startDate ? {startDate: mostRecentEvent.startDate} : {}),
      ...(mostRecentEvent?.friendIds ? {friendIds: [...mostRecentEvent.friendIds]} : {}),
      ...(mostRecentEvent?.tagIds ? {tagIds: [...mostRecentEvent.tagIds]} : {}),
    };
    dispatch(setDialogOpen('Edit Event', {event: newEvent}));
  };

  const openEvent = (event) => {
    dispatch(setSelectedEvent(event));
    dispatch(setDialogOpen('View Event', {event}));
  };

  const openFriend = (friend) => {
    if(!username) return;
    navigateTo(`/${encodeURIComponent(username)}/friends/${encodeURIComponent(friend.id)}`);
  };

  const openCategory = (category) => {
    if(!username) return;
    navigateTo(`/${encodeURIComponent(username)}/categories/${encodeURIComponent(category.id)}`);
  };

  return (
    <main className={`detail-view ${tab === 'events' ? 'detail-view--events' : ''}`.trim()}>
      <section className={`detail-view__hero ${tab === 'events' ? 'detail-view__hero--collapsed' : ''}`.trim()}>
        <div className="detail-view__hero-glow detail-view__hero-glow--trip" aria-hidden="true"/>
        <HeroBar
          backLabel="All Trips"
          onBack={goBack}
          onNewEvent={isOwnMap ? openNewEvent : undefined}
          onEdit={isOwnMap ? openEdit : undefined}
          editLabel="Edit Trip"
          entityTitle={trip.name}
          tab={tab}
          onSelectTab={selectTab}
        />
        <div className="detail-view__hero-content">
          {trip.coverPicUrl ? (
            <div
              className="detail-view__hero-cover"
              style={{backgroundImage: `url(${trip.coverPicUrl})`}}
              aria-label={`${trip.name} cover photo`}
            />
          ) : (
            <div
              className="detail-view__hero-cover detail-view__hero-cover--placeholder"
              aria-hidden="true"
            >
              🧳
            </div>
          )}
          <div className="detail-view__hero-tagline">Trip</div>
          <h1 className="detail-view__hero-title">{trip.name}</h1>
          <div className="detail-view__hero-subtitle">
            {tripSummary
              ? `${formatDateRange(tripSummary.startAt, tripSummary.endAt)} · ${tripEvents.length} ${tripEvents.length === 1 ? 'event' : 'events'}`
              : 'No events on this trip yet'}
          </div>
          {tripFriendIds.length > 0 &&
            <div className="detail-view__hero-meta-row" aria-label="Travelers">
              <MyAvatar
                userAccount={viewingUserAccount}
                style={{height:28, width:28, fontSize:12}}
              />
              {tripFriendIds.slice(0, 8).map(id => (
                <MyAvatar
                  key={id}
                  userAccount={friends[id]}
                  style={{height:28, width:28, fontSize:12}}
                />
              ))}
              {tripFriendIds.length > 8 &&
                <span className="detail-view__chip">+{tripFriendIds.length - 8}</span>
              }
            </div>
          }
        </div>
      </section>

      {tab === 'events' &&
        <EntityEvents
          events={tripEvents}
          emptyText={`Tag events with ${trip.name} and they'll show up here on a timeline.`}
        />
      }

      {tab === 'summary' &&
      <>
      <section className="detail-view__stats-grid" aria-label="Trip stats">
        <StatTile
          label="Events"
          value={tripEvents.length}
          accent="#0ea5e9"
          icon="📅"
          delay={120}
        />
        <StatTile
          label={daySpan === 1 ? 'Day' : 'Days'}
          value={daySpan}
          accent="#6366f1"
          icon="⏳"
          delay={200}
        />
        <StatTile
          label="Places"
          value={placeCount}
          accent="#10b981"
          icon="📍"
          delay={280}
        />
        <StatTile
          label="Travelers"
          value={tripFriendIds.length + 1 /* viewing user */}
          accent="#f59e0b"
          icon="👥"
          delay={360}
        />
      </section>

      {yearEntries.length > 1 &&
        <section className="detail-view__panel">
          <header className="detail-view__panel-header">
            <h2>Events by Year</h2>
          </header>
          <YearChart entries={yearEntries} accentTop="#38bdf8" accentBottom="#0ea5e9"/>
        </section>
      }

      {(topCategories.length > 0 || tripFriendIds.length > 0) &&
        <section className="detail-view__two-up">
          {topCategories.length > 0 &&
            <div className="detail-view__panel">
              <header className="detail-view__panel-header">
                <h2>Top Categories</h2>
              </header>
              <div className="detail-view__rank-list">
                {topCategories.map((item, idx) => (
                  <CategoryRankRow
                    key={item.entity.id}
                    category={item.entity}
                    count={item.count}
                    percent={(item.count / topCategories[0].count) * 100}
                    delay={400 + idx * 80}
                    onClick={() => openCategory(item.entity)}
                  />
                ))}
              </div>
            </div>
          }
          {tripFriendIds.length > 0 &&
            <div className="detail-view__panel">
              <header className="detail-view__panel-header">
                <h2>Travelers</h2>
                <span className="detail-view__panel-subtitle">
                  {tripFriendIds.length + 1} {tripFriendIds.length + 1 === 1 ? 'person' : 'people'}
                </span>
              </header>
              <div className="detail-view__people-grid">
                <div className="detail-view__people-row" style={{cursor:'default'}}>
                  <MyAvatar
                    userAccount={viewingUserAccount}
                    style={{height:36, width:36, fontSize:14}}
                  />
                  <div className="detail-view__people-row-text">
                    <div className="detail-view__people-row-name">
                      {viewingUserAccount?.displayName || 'You'}
                    </div>
                    <div className="detail-view__people-row-meta">Traveler</div>
                  </div>
                </div>
                {tripFriendIds.map(id => {
                  const friend = friends[id];
                  return (
                    <button
                      type="button"
                      className="detail-view__people-row"
                      key={id}
                      onClick={() => openFriend(friend)}
                    >
                      <MyAvatar
                        userAccount={friend}
                        style={{height:36, width:36, fontSize:14}}
                      />
                      <div className="detail-view__people-row-text">
                        <div className="detail-view__people-row-name">{friend.name}</div>
                        <div className="detail-view__people-row-meta">Traveler</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          }
        </section>
      }

      {(topPlace || firstDate) &&
        <section className="detail-view__panel">
          <header className="detail-view__panel-header">
            <h2>Highlights</h2>
          </header>
          <div className="detail-view__rank-list">
            {firstDate &&
              <div className="detail-view__rank-row">
                <div className="detail-view__rank-icon" style={{backgroundColor:'#10b981'}}>🛫</div>
                <div className="detail-view__rank-body">
                  <div className="detail-view__rank-label-row">
                    <span className="detail-view__rank-name">First event</span>
                    <span className="detail-view__rank-count">{formatLongDate(firstDate)}</span>
                  </div>
                </div>
              </div>
            }
            {lastDate && lastDate.getTime() !== firstDate?.getTime() &&
              <div className="detail-view__rank-row">
                <div className="detail-view__rank-icon" style={{backgroundColor:'#f59e0b'}}>🛬</div>
                <div className="detail-view__rank-body">
                  <div className="detail-view__rank-label-row">
                    <span className="detail-view__rank-name">Last event</span>
                    <span className="detail-view__rank-count">{formatLongDate(lastDate)}</span>
                  </div>
                </div>
              </div>
            }
            {topPlace &&
              <div className="detail-view__rank-row">
                <div className="detail-view__rank-icon" style={{backgroundColor:'#6366f1'}}>📍</div>
                <div className="detail-view__rank-body">
                  <div className="detail-view__rank-label-row">
                    <span className="detail-view__rank-name">Most-visited place</span>
                    <span className="detail-view__rank-count">{topPlace.count}</span>
                  </div>
                  <div className="detail-view__event-meta" style={{marginTop:0}}>
                    {topPlace.description}
                  </div>
                </div>
              </div>
            }
          </div>
        </section>
      }

      {recentEvents.length > 0 &&
        <section className="detail-view__panel">
          <header className="detail-view__panel-header">
            <h2>Events on this Trip</h2>
            <span className="detail-view__panel-subtitle">
              {tripEvents.length} total · in date order
            </span>
          </header>
          <div className="detail-view__events-list">
            {recentEvents.map((item, idx) => (
              <EventRow
                key={item.event.id}
                event={item.event}
                startAt={item.startAt}
                category={categories[item.event.categoryId]}
                onClick={() => openEvent(item.event)}
                delay={120 + idx * 50}
              />
            ))}
          </div>
        </section>
      }

      {tripEvents.length === 0 &&
        <section className="detail-view__empty">
          <div className="detail-view__empty-title">No events on this trip yet</div>
          <div>Tag events with {trip.name} and they'll show up here on a timeline.</div>
        </section>
      }
      </>
      }
    </main>
  );
}
